import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { FormField, FormItem, FormControl, FormLabel } from "@/components/ui/form";
import { UseFormReturn } from "react-hook-form";
import { Shield, UserCheck, Settings, EyeOff } from "lucide-react";

interface SetupAssistantStepProps {
  form: UseFormReturn<any>;
}

const SKIP_SETUP_ITEMS = [
  { id: "Location", label: "Location Services", description: "Skip enabling location services" },
  { id: "Restore", label: "Restore from Backup", description: "Skip restore options" },
  { id: "Android", label: "Android Migration", description: "Skip Move from Android" },
  { id: "AppleID", label: "Apple ID Setup", description: "Skip Apple ID sign in" },
  { id: "Terms", label: "Terms & Conditions", description: "Skip T&C acceptance" },
  { id: "Siri", label: "Siri", description: "Skip Siri setup" },
  { id: "Diagnostics", label: "Diagnostics", description: "Skip analytics sharing" },
  { id: "DisplayTone", label: "Display & Brightness", description: "Skip True Tone setup" },
  { id: "Privacy", label: "Privacy", description: "Skip privacy information" },
  { id: "ScreenTime", label: "Screen Time", description: "Skip Screen Time setup" },
  { id: "SoftwareUpdate", label: "Software Update", description: "Skip update prompts" },
  { id: "Passcode", label: "Passcode", description: "Skip passcode setup" },
  { id: "Biometric", label: "Touch ID / Face ID", description: "Skip biometric setup" },
  { id: "Payment", label: "Apple Pay", description: "Skip payment setup" },
  { id: "iCloudDiagnostics", label: "iCloud Analytics", description: "Skip iCloud analytics" },
  { id: "iCloudStorage", label: "iCloud Storage", description: "Skip iCloud storage" },
  { id: "AppStore", label: "App Store", description: "Skip App Store setup" },
  { id: "Accessibility", label: "Accessibility", description: "Skip accessibility options" }
];

export const SetupAssistantStep = ({ form }: SetupAssistantStepProps) => {
  const skipSetupItems = form.watch("skip_setup_items") || [];

  const isAllSelected = skipSetupItems.length === SKIP_SETUP_ITEMS.length;
  const isPartiallySelected = skipSetupItems.length > 0 && skipSetupItems.length < SKIP_SETUP_ITEMS.length;

  const handleSkipItemToggle = (itemId: string) => {
    const currentItems = form.getValues("skip_setup_items") || [];
    const newItems = currentItems.includes(itemId)
      ? currentItems.filter((id: string) => id !== itemId)
      : [...currentItems, itemId];
    form.setValue("skip_setup_items", newItems);
  };

  const handleSelectAll = () => {
    if (isAllSelected) {
      form.setValue("skip_setup_items", []);
    } else {
      form.setValue("skip_setup_items", SKIP_SETUP_ITEMS.map(item => item.id));
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold mb-2">Setup Assistant Options</h2>
        <p className="text-muted-foreground">
          Configure enrollment behavior and skip setup screens
        </p>
      </div>

      <div className="space-y-6">
        {/* MDM Settings */}
      <Card disableHover>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              <CardTitle className="text-lg">MDM Enrollment Settings</CardTitle>
            </div>
            <CardDescription>
              Control how devices interact with MDM during enrollment
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <FormField
              control={form.control}
              name="is_mandatory"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Mandatory Enrollment</FormLabel>
                    <div className="text-sm text-muted-foreground">
                      User cannot skip MDM enrollment during setup
                    </div>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_mdm_removable"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Allow MDM Removal</FormLabel>
                    <div className="text-sm text-muted-foreground">
                      User can remove MDM profile without authorization
                    </div>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="await_device_configured"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Wait for Configuration</FormLabel>
                    <div className="text-sm text-muted-foreground">
                      Device waits for MDM to finish configuration before proceeding
                    </div>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="auto_advance_setup"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Auto-Advance Setup</FormLabel>
                    <div className="text-sm text-muted-foreground">
                      Automatically advance through skipped setup screens
                    </div>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        {/* Skip Setup Items */}
        <Card disableHover>
          <CardHeader>
            <div className="flex items-center gap-2">
              <EyeOff className="h-5 w-5 text-primary" />
              <CardTitle className="text-lg">Skip Setup Screens</CardTitle>
            </div>
            <CardDescription>
              Select which Setup Assistant screens to hide from users
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between mb-4 pb-4 border-b">
              <div className="flex items-center space-x-3">
                <Checkbox
                  id="select-all"
                  checked={isAllSelected || isPartiallySelected}
                  onCheckedChange={handleSelectAll}
                  className={isPartiallySelected ? "data-[state=checked]:bg-primary/50" : ""}
                />
                <Label htmlFor="select-all" className="text-sm font-medium cursor-pointer">
                  Select All
                </Label>
              </div>
              <span className="text-sm text-muted-foreground">
                {skipSetupItems.length} of {SKIP_SETUP_ITEMS.length} selected
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {SKIP_SETUP_ITEMS.map((item) => (
                <div
                  key={item.id}
                  className="flex items-start space-x-3 rounded-lg border p-3 hover:bg-muted/50 transition-colors"
                >
                  <Checkbox
                    id={item.id}
                    checked={skipSetupItems.includes(item.id)}
                    onCheckedChange={() => handleSkipItemToggle(item.id)}
                  />
                  <div className="space-y-1 leading-none">
                    <Label
                      htmlFor={item.id}
                      className="text-sm font-medium cursor-pointer"
                    >
                      {item.label}
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      {item.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
